/**
 * 크로스블록 편집 프리미티브(Phase 2) — 순수 DOM/char 모델 연산(커밋 없음).
 *
 *  - orderSelection: (anchor,focus)를 문서 순서로 정규화.
 *  - deleteRangeAcross: 선택 범위를 삭제하고 끝 블록 잔여를 첫 블록에 병합(첫 블록 타입 유지).
 *      첫/끝 경계 섬이 표/단/중첩(복합)이면 no-op(null 반환) — v1 제외.
 *  - buildCrossBlockClipboard: 첫/끝 부분 + 중간 전체 블록을 저장 폼 blocks 배열로(부분 복사).
 *
 * 오프셋은 inline-format의 char 유닛 공간. 커밋/히스토리는 호출자(cross-block-edit)가 1회 수행.
 */

import { editableContents } from "./caret-nav.js";
import { isPlainIsland, contentLen } from "./cross-block-selection.js";
import { contentToChars, charsToHtml, sliceContentInline } from "../inline-format.js";

/** (anchor,focus)를 문서 순서로 정규화. 반환 없으면 null. */
export function orderSelection(editorEl, sel) {
  if (!sel || !sel.anchorIsland || !sel.focusIsland) return null;
  const list = editableContents(editorEl);
  const ai = list.indexOf(sel.anchorIsland);
  const fi = list.indexOf(sel.focusIsland);
  if (ai < 0 || fi < 0) return null;
  let sIsland = sel.anchorIsland, sOff = sel.anchorOffset;
  let eIsland = sel.focusIsland, eOff = sel.focusOffset;
  if (ai > fi || (ai === fi && sOff > eOff)) {
    sIsland = sel.focusIsland; sOff = sel.focusOffset;
    eIsland = sel.anchorIsland; eOff = sel.anchorOffset;
  }
  return { startIsland: sIsland, startOffset: sOff, endIsland: eIsland, endOffset: eOff, sameIsland: sIsland === eIsland };
}

/** 최상위 .block 형제 목록. */
function topBlocks(editorEl) {
  return [...editorEl.children].filter((c) => c.classList && c.classList.contains("block"));
}

/** 블록의 직속 편집 가능 .content(일반 텍스트 블록만; 표/단 래퍼는 null). */
function editableContentOf(block) {
  if (!block || !block.querySelector) return null;
  return block.querySelector(':scope > .content[contenteditable="true"]');
}

/**
 * 선택 범위 삭제 + 병합. 반환: 캐럿 {island, offset} 또는 null(경계 복합 섬 → no-op).
 * 커밋하지 않는다(호출자가 1회 커밋 → 단일 undo 스텝).
 * opts.collapseEmpty: 병합 결과가 비면(선택이 블록을 완전히 덮음) 빈 첫 블록도 제거하고
 *   인접 편집 블록으로 캐럿 이동(삭제/잘라내기용). 타이핑/붙여넣기는 삽입 대상 블록을 남기려 false.
 */
export function deleteRangeAcross(editorEl, sel, opts) {
  const collapseEmpty = !!(opts && opts.collapseEmpty);
  const o = orderSelection(editorEl, sel);
  if (!o) return null;
  // v1: 첫/끝 경계가 표/단/중첩 섬이면 관여하지 않음(기존 동작 보존).
  if (!isPlainIsland(o.startIsland) || !isPlainIsland(o.endIsland)) return null;

  if (o.sameIsland) {
    const chars = contentToChars(o.startIsland);
    o.startIsland.innerHTML = charsToHtml(chars.slice(0, o.startOffset).concat(chars.slice(o.endOffset)));
    return { island: o.startIsland, offset: o.startOffset };
  }

  const startBlock = o.startIsland.closest(".block");
  const endBlock = o.endIsland.closest(".block");
  const firstKept = contentToChars(o.startIsland).slice(0, o.startOffset);
  const tail = contentToChars(o.endIsland).slice(o.endOffset);
  const merged = firstKept.concat(tail);
  // 첫 블록 다음 ~ 끝 블록(포함)까지 최상위 형제 제거(중간 전체 커버 블록은 통째 삭제 — 표/단 포함).
  if (startBlock && endBlock) {
    let n = startBlock.nextElementSibling;
    while (n && n !== endBlock) {
      const nx = n.nextElementSibling;
      n.remove();
      n = nx;
    }
    endBlock.remove();
  }
  // 완전 커버로 병합 결과가 비었으면(=선택한 블록들 통째 삭제) 빈 첫 블록도 제거 — 편집 가능한 인접 블록이 있을 때.
  if (collapseEmpty && merged.length === 0 && startBlock) {
    const nc = editableContentOf(startBlock.nextElementSibling);
    if (nc) { startBlock.remove(); return { island: nc, offset: 0 }; }
    const pc = editableContentOf(startBlock.previousElementSibling);
    if (pc) { startBlock.remove(); return { island: pc, offset: contentLen(pc) }; }
    // 편집 가능한 인접 블록 없음 → 빈 첫 블록 유지(문서 최소 1블록/캐럿 홈).
  }
  // 끝 블록 잔여를 첫 블록에 병합(첫 블록의 타입/props 유지 — .content만 재작성).
  o.startIsland.innerHTML = charsToHtml(merged);
  return { island: o.startIsland, offset: o.startOffset };
}

/**
 * 부분 복사용 blocks 배열 — 첫/끝은 부분 content, 중간은 원형 블록. doc=getDocument() 최상위 배열.
 * 경계 복합 섬이면 null.
 */
export function buildCrossBlockClipboard(editorEl, sel, doc) {
  const o = orderSelection(editorEl, sel);
  if (!o || !Array.isArray(doc)) return null;
  if (!isPlainIsland(o.startIsland) || !isPlainIsland(o.endIsland)) return null;
  const tops = topBlocks(editorEl);
  const si = tops.indexOf(o.startIsland.closest(".block"));
  const ei = tops.indexOf(o.endIsland.closest(".block"));
  if (si < 0 || ei < 0) return null;

  if (o.sameIsland) {
    if (!doc[si]) return null;
    return [{ ...doc[si], content: sliceContentInline(o.startIsland, o.startOffset, o.endOffset), children: [] }];
  }
  const out = [];
  const startLen = contentToChars(o.startIsland).length;
  if (doc[si]) out.push({ ...doc[si], content: sliceContentInline(o.startIsland, o.startOffset, startLen), children: [] });
  for (let k = si + 1; k < ei; k++) if (doc[k]) out.push(doc[k]);
  if (doc[ei]) out.push({ ...doc[ei], content: sliceContentInline(o.endIsland, 0, o.endOffset), children: [] });
  return out;
}
